export enum PickupRequestStatus {
  Submitted = "Submitted",
  InReview = "InReview",
  Approved = "Approved",
  Invalid = "Invalid",
  Requested = "Requested",
  Accepted = "Accepted",
  Completed = "Completed",
}

export enum PickupRequestPriority {
  Low = "Low",
  Mid = "Mid",
  High = "High",
}

// Read endpoints return the PickupRequestPriority code string; create/update take this int
// instead. Convert between them only via helpers/pickupRequestStatus.ts.
export enum PickupRequestPriorityId {
  Low = 1,
  Mid = 2,
  High = 3,
}

export interface PickupRequestMaterial {
  code: string;
  name: string;
  quantity: number;
}

export interface PickupRequestFormMaterial {
  materialCode: string;
  quantity: number | null;
}

export interface PickupRequestListMaterial {
  amount: number;
  code: string;
}

export interface PickupRequestListItem {
  requestNumber: string;
  statusCode: PickupRequestStatus;
  priorityCode: PickupRequestPriority;
  sourceCompanyId: number | null;
  sourceCompanyName: string | null;
  sourceCompanyRegNumber: number | null;
  targetCompanyId: number | null;
  targetCompanyName: string | null;
  targetCompanyRegNumber: number | null;
  carrierFirstName: string | null;
  carrierLastName: string | null;
  pickupDateFrom: string | null;
  pickupDateTo: string | null;
  submittedDate: string;
  assignedTo: number | null;
  assignedToFullName: string | null;
  municipalityName: string | null;
  zoneName: string | null;
  sourceCompanyAddress: string | null;
  sourceCompanyPhone: string | null;
  comment: string | null;
  priorityReason: string | null;
  createdByFullName: string | null;
  isCreatedByInternal: boolean;
  materials: PickupRequestListMaterial[];
  isFlagged: boolean;
}

export interface PickupRequestsListResponse {
  total: number;
  pickupRequests: PickupRequestListItem[];
}

export interface PickupRequestFilterParameter {
  propertyName: string;
  values: string;
}

export interface PickupRequestSorting {
  propertyName: string;
  isAscending: boolean;
}

export interface PickupRequestsListRequest {
  filteringParameters: PickupRequestFilterParameter[];
  limit?: number;
  skip: number;
  sortingBy: PickupRequestSorting;
}

export interface PickupRequestCompany {
  companyId: number;
  displayName: string | null;
   // RPRA registration number (Companies.RPRARegistrationNumber); the eTracks participant number is regNumber.
  registrationNumber: string | null;
  regNumber?: number | null;
  phone: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  municipalityName: string | null;
  zoneName: string | null;
}

export interface PickupRequestDetails {
  requestNumber: string | null;
  status: PickupRequestStatus;
  priority: PickupRequestPriority;
  priorityReason: string | null;
  sourceCompanyId: number | null;
  targetCompanyId: number | null;
  source: PickupRequestCompany | null;
  target: PickupRequestCompany | null;
  assignedCarrierId: number | null;
  carrierFirstName: string | null;
  carrierLastName: string | null;
  assignedTo: number | null;
  assignedToFullName: string | null;
  createdByFullName: string | null;
  isCreatedByInternal: boolean;
  comment: string | null;
  invalidationComment: string | null;
  otherReasonText: string | null;
  isFlagged: boolean;
  flagComment: string | null;
  showFlagToParticipants: boolean;
  hasLinkedTransaction?: boolean;
  differsFromLinkedTransaction?: boolean;
  linkedTransactionNumber: string | null;
  shipmentTransactionNumber?: string | null;
  pickupDateFrom: string | null;
  pickupDateTo: string | null;
  submittedDate: string;
  materials: PickupRequestMaterial[];
  totalQuantity: number;
  reasonIds: number[];
  updatedOn: string | null;
  updatedBy: number | null;
  updatedByFullName: string | null;
}

export interface CreatePickupRequest {
  sourceCompanyId: number | null;
  targetCompanyId: number | null;
  assignedCarrierId: number | null;
  pickupDateFrom: string;
  pickupDateTo: string | null;
  materials: PickupRequestFormMaterial[];
  reasonIds: number[];
  otherReasonText: string | null;
  comment: string | null;
  priority: PickupRequestPriorityId;
  priorityReason: string | null;
  linkedTransactionNumber: string | null;
}

// Mirrors UpdatePickupRequestDto exactly - MissingMemberHandling.Error refuses the whole
// request on an unrecognised member, so no field outside this set (no sourceCompanyId, no
// status) may ever be added here.
export interface UpdatePickupRequest {
  targetCompanyId: number | null;
  assignedCarrierId: number | null;
  materials: PickupRequestFormMaterial[];
  pickupDateFrom: string;
  pickupDateTo: string | null;
  reasonIds: number[];
  otherReasonText: string | null;
  comment: string | null;
  priority: PickupRequestPriorityId;
  priorityReason: string | null;
}

export interface PickupRequestReason {
  reasonId: number;
  code: string;
  reasonName: string;
}

export interface PickupRequestDocument {
  documentId: number;
  name: string;
  size: number;
  description: string | null;
  documentType: string | null;
  createdDate: string;
  isDeletable: boolean;
}

export interface ParticipantOption {
  companyId: number;
  displayName: string;
  regNumber?: number;
  registrationNumber?: string | null;
  // RPRA registration number (Companies.RPRARegistrationNumber); the eTracks participant number is regNumber.
  // Both are optional: a company may simply have no business address on file.
  addressLine1?: string;
  city?: string;
}

export interface PickupRequestFormValues {
  collectorId: number | null;
  haulerId: number | null;
  carrierId: number | null;
  materials: PickupRequestFormMaterial[];
  pickupDateFrom: string | null;
  pickupDateTo: string | null;
  comment: string | null;
  priority: PickupRequestPriorityId | null;
  priorityReason: string | null;
}
